// MultiSelectDemographicBar.tsx
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
} from '@chakra-ui/react';
import { FiPlusCircle } from 'react-icons/fi';

import React, { memo } from 'react';
import { ConversationType } from '../../generated/graphql';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { DemographicType } from './DemographicSelector';

interface MultiSelectDemographicBarProps {
  selectedDemographics: DemographicType[];
  onRemoveDemographic: (demographic: DemographicType) => void;
  onAddDemographic: () => void;
  maxSelections?: number;
  type: ConversationType;
}

const MultiSelectDemographicBar: React.FC<MultiSelectDemographicBarProps> = ({
  selectedDemographics,
  onRemoveDemographic,
  onAddDemographic,
  maxSelections = 6,
  type,
}) => {
  // Function to get tag color based on demographic key (for demo purposes)
  const getTagColor = (key: string): string => {
    switch (key) {
      case 'gender':
        return '#F9D5FF';
      case 'ethnicity':
        return '#E3F9B9';
      case 'region':
        return '#D9C3F6';
      case 'party':
        return '#FFD9E8';
      case 'chamber':
        return '#D9F1F6';
      default:
        return '#E6E6E6';
    }
  };

  return (
    <Stack spacing={2} width="full">
      <Box
        borderWidth="1px"
        borderRadius="lg"
        borderColor="gray.200"
        bg={HelivanColor.background.lightestGrey}
        p={1.5}
        width="100%"
        position="relative"
        onClick={onAddDemographic}
        cursor="pointer"
      >
        <Flex
          overflowX="auto"
          width={'full'}
          alignItems="center"
          justifyContent="space-between"
          css={{
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
          }}
        >
          <HStack spacing={2}>
            {selectedDemographics.map((demographic, idx) => (
              <Tag
                key={`${demographic.key}-${demographic.value}`}
                size="sm"
                borderRadius="4px"
                variant="solid"
                bg={getTagColor(demographic.key)}
                color="black"
                padding="2px 4px"
                mr={2}
              >
                <TagLabel>
                  <HelivanText variant="smallBody" colorOverride="black">
                    {demographic.value}
                  </HelivanText>
                </TagLabel>
                <TagCloseButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveDemographic(demographic);
                  }}
                  _hover={{ bg: 'rgba(0, 0, 0, 0.1)' }}
                />
              </Tag>
            ))}
          </HStack>

          <IconButton
            aria-label="Toggle demographic selection"
            icon={<FiPlusCircle size={20} />}
            variant="ghost"
            borderRadius="full"
            size="sm"
            _hover={{ bg: undefined }}
          />
        </Flex>
      </Box>

      <HelivanText variant="smallBody">
        {`You may select up to ${maxSelections} demographics.`}
      </HelivanText>
    </Stack>
  );
};

export default memo(MultiSelectDemographicBar);
